import { SiteSettingsUpdate } from "@divespots/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { Button } from "../components/Button.tsx";
import { formatTimestamp } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { Field, Section } from "./ui.tsx";

/** Admin only: who may register, and how many new accounts per hour; the numbers behind it. */
export function SettingsPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: ({ signal }) => api.getSettings(signal),
  });
  const settings = q.data;

  const [open, setOpen] = useState(false);
  const [max, setMax] = useState("");
  const [maxError, setMaxError] = useState<string | undefined>();
  const [banner, setBanner] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // the form starts from what the server has, and follows it after a save or a refetch
  const stamp = settings
    ? `${settings.updatedAt}|${settings.registrationOpen}|${settings.maxNewAccountsPerHour}`
    : "";
  useEffect(() => {
    if (!settings) return;
    setOpen(settings.registrationOpen);
    setMax(String(settings.maxNewAccountsPerHour));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp]);

  const save = useMutation({
    mutationFn: (body: SiteSettingsUpdate) => api.updateSettings(body),
    onSuccess: async (next) => {
      setBanner(null);
      setSaved(true);
      qc.setQueryData(["admin", "settings"], next);
    },
    onError: async (e) => {
      setSaved(false);
      if (isApiRequestError(e) && e.code === "conflict") {
        // mail is not configured: registration cannot be opened (the server is the judge)
        setBanner(t("admin.settings.mailConflict"));
        setOpen(false);
        await qc.invalidateQueries({ queryKey: ["admin", "settings"] });
        return;
      }
      setBanner(t("admin.err.save"));
    },
  });

  if (q.isPending) return <p role="status">{t("common.loading")}</p>;
  if (q.isError || !settings) {
    return (
      <p className="adm-error" role="alert">
        {t("admin.err.load")}
      </p>
    );
  }

  const submit = () => {
    setSaved(false);
    setBanner(null);
    setMaxError(undefined);
    const n = /^\d{1,4}$/.test(max.trim()) ? Number(max.trim()) : Number.NaN;
    const parsed = SiteSettingsUpdate.safeParse({
      registrationOpen: open,
      maxNewAccountsPerHour: n,
    });
    if (!parsed.success) return setMaxError(t("admin.settings.maxInvalid"));
    save.mutate(parsed.data);
  };

  const noMail = !settings.mailConfigured;
  return (
    <div className="grid gap-6">
      <h1 className="ds-text-title-1 m-0">{t("admin.settings.title")}</h1>

      <Section title={t("admin.settings.registration")}>
        {noMail ? (
          <p className="adm-warning" role="alert" data-testid="no-mail">
            <TriangleAlert aria-hidden="true" size={20} strokeWidth={1.75} />
            {t("admin.settings.noMail")}
          </p>
        ) : null}
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="adm-check">
            <input
              type="checkbox"
              role="switch"
              checked={open}
              disabled={noMail && !open}
              onChange={(e) => setOpen(e.target.checked)}
            />
            <span>{t("admin.settings.allow")}</span>
          </label>
          <Field
            label={t("admin.settings.max")}
            hint={t("admin.settings.maxHint")}
            error={maxError}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                className="adm-input"
                inputMode="numeric"
                autoComplete="off"
                value={max}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => setMax(e.target.value)}
              />
            )}
          </Field>
          {banner ? (
            <p className="adm-error m-0" role="alert">
              {banner}
            </p>
          ) : null}
          {saved ? (
            <p className="adm-ok m-0" role="status">
              {t("admin.settings.saved")}
            </p>
          ) : null}
          <div>
            <Button type="submit" variant="filled" disabled={save.isPending}>
              {t("admin.settings.save")}
            </Button>
          </div>
        </form>

        <dl className="adm-stats">
          <div>
            <dt className="ds-text-footnote text-label-secondary">
              {t("admin.settings.newLastHour")}
            </dt>
            <dd className="ds-text-title-2 m-0" data-testid="new-last-hour">
              {settings.newAccountsLastHour}
            </dd>
          </div>
          <div>
            <dt className="ds-text-footnote text-label-secondary">
              {t("admin.settings.refusedLastHour")}
            </dt>
            <dd className="ds-text-title-2 m-0" data-testid="refused-last-hour">
              {settings.refusedSignupsLastHour}
            </dd>
          </div>
        </dl>
        {settings.updatedAt ? (
          <p className="ds-text-footnote text-label-secondary m-0">
            {t("admin.settings.updatedAt", { date: formatTimestamp(settings.updatedAt, locale) })}
          </p>
        ) : null}
      </Section>
    </div>
  );
}
